import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { DesktopWindows } from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const WorkspaceOccupancyPie = ({ workspaces = [] }) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("all");

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find(
        (workspace) => String(workspace.id) === selectedWorkspaceId
      ) || null,
    [workspaces, selectedWorkspaceId]
  );

  const displayedWorkspaces = useMemo(
    () =>
      selectedWorkspaceId === "all"
        ? workspaces
        : selectedWorkspace
          ? [selectedWorkspace]
          : [],
    [workspaces, selectedWorkspaceId, selectedWorkspace]
  );

  const stats = useMemo(() => {
    const total = displayedWorkspaces.reduce(
      (sum, workspace) => sum + (workspace.workstations?.length || 0),
      0
    );

    const occupied = displayedWorkspaces.reduce(
      (sum, workspace) =>
        sum +
        (workspace.workstations || []).filter(
          (station) => station.status === "occupied"
        ).length,
      0
    );

    return {
      total,
      occupied,
      empty: Math.max(0, total - occupied),
    };
  }, [displayedWorkspaces]);

  const hasData = stats.total > 0;
  const occupiedPercentage = hasData ? (stats.occupied / stats.total) * 100 : 0;
  const emptyPercentage = hasData ? (stats.empty / stats.total) * 100 : 0;

  const workspaceName =
    selectedWorkspaceId === "all"
      ? "All Workspaces"
      : selectedWorkspace?.name || "Workspace";

  const workspaceColor = selectedWorkspace?.color || "#2563EB";

  const data = useMemo(
    () =>
      hasData
        ? [
            {
              name: "Occupied",
              value: stats.occupied,
              percentage: occupiedPercentage,
            },
            {
              name: "Empty",
              value: stats.empty,
              percentage: emptyPercentage,
            },
          ]
        : [{ name: "No data", value: 1, percentage: 0 }],
    [
      hasData,
      stats.occupied,
      stats.empty,
      occupiedPercentage,
      emptyPercentage,
    ]
  );

  const colors = hasData ? [workspaceColor, "#E2E8F0"] : ["#D4D4D4"];

  const legendItems = [
    {
      name: "Occupied",
      value: stats.occupied,
      color: "#2563EB",
      key: "occupied",
    },
    {
      name: "Empty",
      value: stats.empty,
      color: "#EF4444",
      key: "empty",
    },
    {
      name: "Total",
      value: stats.total,
      color: null,
      key: "total",
    },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length || !hasData) return null;

    const item = payload[0].payload;

    return (
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 2,
          px: 1.5,
          py: 1,
          boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2D3748" }}>
          {item.name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
          {item.value} workstation{item.value === 1 ? "" : "s"}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
          {Number(item.percentage || 0).toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
     <Card
                sx={{
                    width: "100%",
                    minWidth: 0,
                    height: "100%",
                    boxSizing: "border-box",
                    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    border: "1px solid rgba(64, 81, 138, 0.12)",
                    borderRadius: 3,
                    boxShadow: "0 10px 28px rgba(31, 45, 84, 0.12)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": { boxShadow: "0 12px 32px rgba(31, 45, 84, 0.16)" },
                    overflow: "visible",
                }}
            >
      <CardContent
                      sx={{
                          p: { xs: 1.5, sm: 2.25, md: 2.5 },
                          "&:last-child": { pb: { xs: 1.5, sm: 2.25, md: 2.5 } },
                          minWidth: 0,
                          boxSizing: "border-box",
                      }}
                  >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1.25,
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.25, sm: 1.5 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                p: { xs: 1.2, sm: 1.5 },
                background: "rgba(102, 126, 234, 0.1)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DesktopWindows
                sx={{ color: "#667eea", fontSize: { xs: 22, sm: 24 } }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#2d3748",
                  lineHeight: 1.25,
                  fontSize: { xs: "1.05rem", sm: "1.25rem" },
                }}
              >
                Workspace Occupancy
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}
              >
                {workspaceName}
              </Typography>
            </Box>
          </Box>

          <FormControl
            size="small"
            sx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 145 },
              flexShrink: 0,
            }}
          >
            <Select
              value={selectedWorkspaceId}
              onChange={(event) => setSelectedWorkspaceId(event.target.value)}
              inputProps={{ "aria-label": "Select workspace" }}
              sx={{
                height: 36,
                fontSize: 13,
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <MenuItem value="all">All Workspaces</MenuItem>
              {workspaces.map((workspace) => (
                <MenuItem key={workspace.id} value={String(workspace.id)}>
                  {workspace.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Total, occupied and empty counts */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2.5 },
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            Total: <Box component="strong" sx={{ color: "#2D3748" }}>{stats.total}</Box>
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            Occupied: <Box component="strong" sx={{ color: "#2563EB" }}>{stats.occupied}</Box>
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            Empty: <Box component="strong" sx={{ color: "#EF4444" }}>{stats.empty}</Box>
          </Typography>
        </Box>

        {/* Donut chart and legend */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 280, sm: 260, md: 290 },
            py: { xs: 1, md: 2 },
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              width: "100%",
              minWidth: 0,
              maxWidth: { xs: 360, sm: "none" },
              height: { xs: 260, sm: 290, md: 320 },
              position: "relative",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="90%"
                  paddingAngle={hasData ? 2 : 0}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={colors[index] || "#D4D4D4"}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
                width: "48%",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 23, sm: 27 },
                  fontWeight: 700,
                  color: hasData ? workspaceColor : "#94A3B8",
                  lineHeight: 1.1,
                }}
              >
                {hasData ? `${occupiedPercentage.toFixed(1)}%` : "No data"}
              </Typography>
              {hasData && (
                <Typography sx={{ fontSize: 12, color: "#64748B", mt: 0.8 }}>
                  Occupied
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
              justifyContent: { xs: "center", sm: "center" },
              alignItems: { xs: "flex-start", sm: "stretch" },
              gap: { xs: 1.5, sm: 2 },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 115 },
              flexShrink: 0,
            }}
          >
            {legendItems.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: { xs: 75, sm: "auto" },
                }}
              >
                {item.color && (
                  <Box
                    sx={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 700, color: "#64748B" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {!hasData && (
          <Typography
            sx={{
              textAlign: "center",
              fontSize: 12,
              color: "#94A3B8",
              mt: 1,
            }}
          >
            No workstation data available for this selection.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkspaceOccupancyPie;

